import React, { useState, useEffect } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { projectsAPI, vehiclesAPI } from '../../helpers/api';

interface Vehicle {
  id: string;
  plate: string;
  brand: string | null;
  model: string | null;
  type: string;
  status: string;
}

interface ProjectVehicle {
  id: string;
  vehicle: Vehicle;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { name: string };
}

interface ProjectVehiclesModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
  onUpdate?: () => void;
}

const typeLabels: Record<string, string> = {
  COMPANY: 'Şirket Aracı',
  RENTAL: 'Kiralık',
  MACHINERY: 'İş Makinesi',
};

const typeIcons: Record<string, string> = {
  COMPANY: 'ri-truck-line',
  RENTAL: 'ri-car-washing-line',
  MACHINERY: 'ri-steering-2-line',
};

const typeColors: Record<string, string> = {
  COMPANY: 'text-primary',
  RENTAL: 'text-info',
  MACHINERY: 'text-warning',
};

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  MAINTENANCE: 'bg-warning bg-opacity-10 text-warning',
  INACTIVE: 'bg-secondary bg-opacity-10 text-secondary',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  MAINTENANCE: 'Bakımda',
  INACTIVE: 'Pasif',
};

const ProjectVehiclesModal: React.FC<ProjectVehiclesModalProps> = ({
  isOpen,
  toggle,
  project,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [vehicles, setVehicles] = useState<ProjectVehicle[]>([]);
  const [allVehicles, setAllVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && project) {
      fetchData();
    }
  }, [isOpen, project]);

  const fetchData = async () => {
    if (!project) return;

    try {
      setLoading(true);
      const [projectRes, vehiclesRes] = await Promise.all([
        projectsAPI.getOne(project.id),
        vehiclesAPI.getAll(),
      ]);
      setVehicles(projectRes.data.projectVehicles || []);
      setAllVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Araçlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (vehicleId: string) => {
    if (!project) return;

    try {
      setAdding(vehicleId);
      await vehiclesAPI.assignToProject(vehicleId, project.id);
      toast.success('Araç projeye atandı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama başarısız');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (vehicleId: string) => {
    if (!project) return;
    if (!window.confirm('Aracı projeden çıkarmak istediğinize emin misiniz?')) return;

    try {
      setRemoving(vehicleId);
      await vehiclesAPI.removeFromProject(vehicleId, project.id);
      toast.success('Araç projeden çıkarıldı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setRemoving(null);
    }
  };

  const assignedVehicleIds = vehicles.map(pv => pv.vehicle.id);
  const unassignedVehicles = allVehicles.filter(v => !assignedVehicleIds.includes(v.id));

  const filteredUnassigned = unassignedVehicles.filter(
    (v) =>
      v.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      typeLabels[v.type]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!project) return null;

  const headerActions = (
    <>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={() => setShowAddPanel(!showAddPanel)}
        title={showAddPanel ? 'Listeye Dön' : 'Araç Ekle'}
        style={{ color: showAddPanel ? '#f06548' : '#0ab39c' }}
      >
        <i className={showAddPanel ? 'ri-arrow-left-line' : 'ri-add-line'}></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  const vehiclesByType = vehicles.reduce((acc, pv) => {
    const type = pv.vehicle.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(pv.vehicle);
    return acc;
  }, {} as Record<string, Vehicle[]>);

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Proje Araçları"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>{project.name}</h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {project.city.name} • {project.code}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : showAddPanel ? (
          /* Add Vehicle Panel */
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Araç Ekle
            </div>
            <Input
              type="text"
              placeholder="Plaka, marka veya tip ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            {filteredUnassigned.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">
                  {searchTerm ? 'Aramayla eşleşen araç bulunamadı' : 'Atanabilecek araç bulunmuyor'}
                </p>
              </div>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {filteredUnassigned.map((v, idx) => (
                  <div
                    key={v.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < filteredUnassigned.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <div className="d-flex align-items-center">
                        <i className={`${typeIcons[v.type] || 'ri-car-line'} me-2 ${typeColors[v.type] || 'text-primary'}`}></i>
                        <span style={{ fontWeight: 500 }}>{v.plate}</span>
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.85rem', marginLeft: '1.5rem' }}>
                        {v.brand} {v.model} • {typeLabels[v.type] || v.type}
                      </div>
                    </div>
                    <Button color="success" size="sm" onClick={() => handleAssign(v.id)} disabled={adding === v.id}>
                      {adding === v.id ? <Spinner size="sm" /> : <i className="ri-add-line"></i>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {vehicles.length > 0 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Özet
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(vehiclesByType).map(([type, vList]) => (
                    <div key={type} className="px-3 py-2 rounded-3" style={{ fontSize: '0.85rem', background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                      <i className={`${typeIcons[type] || 'ri-car-line'} ${typeColors[type] || 'text-primary'} me-1`}></i>
                      <span style={{ fontWeight: 500 }}>{vList.length}</span>
                      <span className="ms-1">{typeLabels[type] || type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vehicles List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Atanmış Araçlar
                </span>
                <span className="text-muted small">{vehicles.length} araç</span>
              </div>
              {vehicles.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">Bu projeye henüz araç atanmamış</p>
                  <Button color="primary" size="sm" onClick={() => setShowAddPanel(true)}>
                    <i className="ri-add-line me-1"></i>Araç Ekle
                  </Button>
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {vehicles.map((pv, idx) => (
                    <div
                      key={pv.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < vehicles.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center mb-1">
                            <i className={`${typeIcons[pv.vehicle.type] || 'ri-car-line'} me-2 ${typeColors[pv.vehicle.type] || 'text-primary'}`}></i>
                            <span style={{ fontWeight: 500 }}>{pv.vehicle.plate}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem', marginLeft: '1.5rem' }}>
                            {pv.vehicle.brand} {pv.vehicle.model} • {typeLabels[pv.vehicle.type] || pv.vehicle.type}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className={`px-2 py-1 rounded-pill small me-2 ${statusStyles[pv.vehicle.status]}`} style={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {statusLabels[pv.vehicle.status]}
                          </span>
                          <Button color="danger" size="sm" outline onClick={() => handleRemove(pv.vehicle.id)} disabled={removing === pv.vehicle.id} style={{ padding: '0.15rem 0.4rem' }}>
                            {removing === pv.vehicle.id ? <Spinner size="sm" /> : <i className="ri-delete-bin-line" style={{ fontSize: '0.8rem' }}></i>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default ProjectVehiclesModal;
